import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Activity, CreateActivityInput, UpdateActivityInput } from '../types/activity';

function activitiesRef(userId: string) {
  return collection(db, 'users', userId, 'activities');
}

/**
 * Log a new activity.
 */
export async function addActivity(
  userId: string,
  input: CreateActivityInput
): Promise<{ success: true; id: string } | { success: false; error: string }> {
  try {
    const docRef = await addDoc(activitiesRef(userId), {
      plantId: input.plantId,
      plantName: input.plantName,
      plantPhotoURL: input.plantPhotoURL,
      type: input.type,
      note: input.note ?? null,
      createdAt: serverTimestamp(),
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Failed to add activity:', error);
    return { success: false, error: 'Could not save activity. Please try again.' };
  }
}

/**
 * Update an existing activity (e.g., edit note).
 */
export async function updateActivity(
  userId: string,
  activityId: string,
  input: UpdateActivityInput
): Promise<{ success: true } | { success: false; error: string }> {
  try {
    const activityRef = doc(db, 'users', userId, 'activities', activityId);
    await updateDoc(activityRef, { ...input });
    return { success: true };
  } catch (error) {
    console.error('Failed to update activity:', error);
    return { success: false, error: 'Could not update activity.' };
  }
}

/**
 * Delete an activity.
 */
export async function deleteActivity(
  userId: string,
  activityId: string
): Promise<{ success: true } | { success: false; error: string }> {
  try {
    await deleteDoc(doc(db, 'users', userId, 'activities', activityId));
    return { success: true };
  } catch (error) {
    console.error('Failed to delete activity:', error);
    return { success: false, error: 'Could not delete activity.' };
  }
}

/**
 * Subscribe to all activities for a user (newest first), limited.
 */
export function subscribeToActivities(
  userId: string,
  maxResults: number,
  callback: (activities: Activity[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(
    activitiesRef(userId),
    orderBy('createdAt', 'desc'),
    limit(maxResults)
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const activities = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Activity[];
      callback(activities);
    },
    (error) => {
      console.error('Activities subscription error:', error);
      onError?.(error);
    }
  );
}

/**
 * Subscribe to activities for a specific plant (newest first), limited.
 */
export function subscribeToPlantActivities(
  userId: string,
  plantId: string,
  maxResults: number,
  callback: (activities: Activity[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(
    activitiesRef(userId),
    where('plantId', '==', plantId),
    orderBy('createdAt', 'desc'),
    limit(maxResults)
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const activities = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Activity[];
      callback(activities);
    },
    (error) => {
      console.error('Plant activities subscription error:', error);
      onError?.(error);
    }
  );
}
